import React, { useMemo, useState } from "react";
import { toast } from "react-toastify";
import {
    Card,
    CardContent,
    CardFooter,
    CardHeader,
    CardTitle,
} from "../../components/misc/Card";
import { Button } from "../../components/misc/Button";
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from "../../components/misc/Table";
import {
    Pagination,
    PaginationContent,
    PaginationEllipsis,
    PaginationItem,
    PaginationLink,
    PaginationNext,
    PaginationPrevious,
} from "../../components/misc/Pagination";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "../../components/misc/Select";
import {
    Popover,
    PopoverContent,
    PopoverTrigger,
} from "../../components/misc/Popover";
import CustomDatePicker from "../../components/misc/CutomDatePicker";
import { Badge } from "../../components/calendar/Badge";
import Checkbox from "../../components/misc/Checkbox";
import Spinner from "../../components/misc/Spinner";
import { Input } from "../../components/misc/Input";
import ReconfirmOverlay from "../../components/misc/ReconfirmOverlay";
import {
    CalendarClock,
    CalendarIcon,
    ChevronDown,
    ChevronUp,
    Clock,
} from "lucide-react";
import { format } from "date-fns";
import { cn } from "../../utils/classMerger";
import {
    FailedPost,
    FailedPostSortColumn,
    FailedPostStatus,
    SortDirection,
} from "./types";

const COLUMN_COUNT = 9;

const STATUS_LABEL: Record<FailedPostStatus, string> = {
    [FailedPostStatus.Error]: "Error",
    [FailedPostStatus.Retrying]: "Retrying",
};

const HOUR_OPTIONS = Array.from({ length: 12 }, (_, i) => i + 1);
const MINUTE_OPTIONS = Array.from({ length: 60 }, (_, i) => i);

// Minimum lead time for a manual reschedule — matches the backend default (now + 5 min); the
// publish pipeline needs a few minutes to prepare, so disallow times closer than this to "now".
const MIN_RESCHEDULE_LEAD_MS = 5 * 60 * 1000;

function formatPlatform(platform: string): string {
    return platform
        .split("_")
        .map((word) => word.charAt(0) + word.slice(1).toLowerCase())
        .join(" ");
}

function formatDateTime(value: string): string {
    return new Date(value).toLocaleString("en-US", {
        month: "short",
        day: "numeric",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
        hour12: true,
    });
}

interface FailedPostsListProps {
    posts: FailedPost[];
    loading: boolean;
    page: number;
    setPage: (page: number) => void;
    limit: number;
    total: number;
    searchTerm: string;
    setSearchTerm: (value: string) => void;
    statusFilter: string;
    setStatusFilter: (value: string) => void;
    platformFilter: string;
    setPlatformFilter: (value: string) => void;
    platforms: string[];
    fromDate: Date;
    toDate: Date;
    setFromDate: (date: Date) => void;
    setToDate: (date: Date) => void;
    sortBy?: FailedPostSortColumn;
    sortDirection: SortDirection;
    onSortChange: (column: FailedPostSortColumn, direction: SortDirection) => void;
    onReschedule: (postIds: string[], scheduledFor?: string) => Promise<void>;
    isMobile: boolean;
}

const FailedPostsList: React.FC<FailedPostsListProps> = ({
    posts,
    loading,
    page,
    setPage,
    limit,
    total,
    searchTerm,
    setSearchTerm,
    statusFilter,
    setStatusFilter,
    platformFilter,
    setPlatformFilter,
    platforms,
    fromDate,
    toDate,
    setFromDate,
    setToDate,
    sortBy,
    sortDirection,
    onSortChange,
    onReschedule,
    isMobile,
}) => {
    const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
    const [rescheduleDate, setRescheduleDate] = useState<Date>(
        () => new Date(Date.now() + 5 * 60 * 1000)
    );
    const [isCalendarOpen, setIsCalendarOpen] = useState(false);
    const [isFromOpen, setIsFromOpen] = useState(false);
    const [isToOpen, setIsToOpen] = useState(false);
    const [confirmOpen, setConfirmOpen] = useState(false);
    const [submitting, setSubmitting] = useState(false);

    const totalPages = Math.ceil(total / limit);

    const visibleIds = useMemo(() => posts.map((p) => p.postId), [posts]);
    const allVisibleSelected =
        visibleIds.length > 0 && visibleIds.every((id) => selectedIds.has(id));

    const period = rescheduleDate.getHours() >= 12 ? "PM" : "AM";
    const displayHour = rescheduleDate.getHours() % 12 || 12;
    const minute = rescheduleDate.getMinutes();
    const isTooSoon =
        rescheduleDate.getTime() < Date.now() + MIN_RESCHEDULE_LEAD_MS;

    const triggerWidth = isMobile ? "w-full" : "w-[180px]";

    const toggleSelectAll = (checked: boolean): void => {
        setSelectedIds((prev) => {
            const next = new Set(prev);
            visibleIds.forEach((id) =>
                checked ? next.add(id) : next.delete(id)
            );
            return next;
        });
    };

    const toggleOne = (id: string, checked: boolean): void => {
        setSelectedIds((prev) => {
            const next = new Set(prev);
            if (checked) next.add(id);
            else next.delete(id);
            return next;
        });
    };

    const clearSelection = (): void => setSelectedIds(new Set());

    const handleDateChange = (picked: Date): void => {
        const next = new Date(rescheduleDate);
        next.setFullYear(
            picked.getFullYear(),
            picked.getMonth(),
            picked.getDate()
        );
        setRescheduleDate(next);
    };

    const handleHourChange = (value: string): void => {
        const hour12 = parseInt(value, 10) % 12;
        const next = new Date(rescheduleDate);
        next.setHours(period === "PM" ? hour12 + 12 : hour12);
        setRescheduleDate(next);
    };

    const handleMinuteChange = (value: string): void => {
        const next = new Date(rescheduleDate);
        next.setMinutes(parseInt(value, 10));
        setRescheduleDate(next);
    };

    const handlePeriodChange = (value: string): void => {
        const hour12 = rescheduleDate.getHours() % 12;
        const next = new Date(rescheduleDate);
        next.setHours(value === "PM" ? hour12 + 12 : hour12);
        setRescheduleDate(next);
    };

    const handleSort = (column: FailedPostSortColumn): void => {
        const direction: SortDirection =
            sortBy === column && sortDirection === "asc" ? "desc" : "asc";
        onSortChange(column, direction);
        setPage(1);
    };

    const renderSortIcon = (column: FailedPostSortColumn): React.ReactNode => {
        if (sortBy !== column) return null;
        return sortDirection === "asc" ? (
            <ChevronUp className="h-4 w-4 inline ml-1" />
        ) : (
            <ChevronDown className="h-4 w-4 inline ml-1" />
        );
    };

    const sortableHead = (
        label: string,
        column: FailedPostSortColumn,
        className?: string
    ): React.ReactNode => (
        <TableHead
            role="button"
            tabIndex={0}
            aria-label={`Sort by ${label}`}
            className={cn(
                "cursor-pointer select-none hover:bg-gray-50",
                className
            )}
            onClick={() => handleSort(column)}
            onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    handleSort(column);
                }
            }}
        >
            {label}
            {renderSortIcon(column)}
        </TableHead>
    );

    const handleRescheduleClick = (): void => {
        // Re-check against a fresh "now" — guards the case where time passed since render.
        if (rescheduleDate.getTime() < Date.now() + MIN_RESCHEDULE_LEAD_MS) {
            toast.error(
                "Pick a reschedule time at least 5 minutes from now - the post needs a moment to prepare before publishing."
            );
            return;
        }
        setConfirmOpen(true);
    };

    const handleConfirm = async (): Promise<void> => {
        // Backstop: time may have elapsed while the confirm dialog was open.
        if (rescheduleDate.getTime() < Date.now() + MIN_RESCHEDULE_LEAD_MS) {
            toast.error(
                "Reschedule time must be at least 5 minutes from now."
            );
            setConfirmOpen(false);
            return;
        }
        setConfirmOpen(false);
        setSubmitting(true);
        try {
            await onReschedule(
                Array.from(selectedIds),
                rescheduleDate.toISOString()
            );
            clearSelection();
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <Card>
            <CardHeader>
                <CardTitle className="text-left">
                    Failed Posts ({total})
                </CardTitle>

                <div
                    className={cn(
                        "flex gap-3 pt-4",
                        isMobile
                            ? "flex-col"
                            : "flex-row flex-wrap items-center"
                    )}
                >
                    <Input
                        type="text"
                        placeholder="Search by owner name or email"
                        value={searchTerm}
                        onChange={(e) => {
                            setSearchTerm(e.target.value);
                            setPage(1);
                        }}
                        className={cn(isMobile ? "w-full" : "min-w-[220px] flex-1")}
                    />
                    <Select
                        value={platformFilter}
                        onValueChange={(value) => {
                            setPlatformFilter(value);
                            setPage(1);
                        }}
                    >
                        <SelectTrigger className={triggerWidth}>
                            <SelectValue placeholder="All Platforms" />
                        </SelectTrigger>
                        <SelectContent>
                            <SelectItem value="All">All Platforms</SelectItem>
                            {platforms.map((platform) => (
                                <SelectItem key={platform} value={platform}>
                                    {formatPlatform(platform)}
                                </SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                    <Select
                        value={statusFilter}
                        onValueChange={(value) => {
                            setStatusFilter(value);
                            setPage(1);
                        }}
                    >
                        <SelectTrigger className={triggerWidth}>
                            <SelectValue placeholder="All Statuses" />
                        </SelectTrigger>
                        <SelectContent>
                            <SelectItem value="All">All Statuses</SelectItem>
                            <SelectItem value={FailedPostStatus.Error}>
                                Error
                            </SelectItem>
                            <SelectItem value={FailedPostStatus.Retrying}>
                                Retrying
                            </SelectItem>
                        </SelectContent>
                    </Select>
                    <div
                        className={cn(
                            "flex gap-1",
                            isMobile ? "flex-col items-stretch" : "items-center"
                        )}
                    >
                        <span className="text-sm text-gray-500">From</span>
                        <Popover open={isFromOpen} onOpenChange={setIsFromOpen}>
                            <PopoverTrigger asChild>
                                <Button
                                    variant="outline"
                                    className={cn(
                                        "justify-start text-left font-normal",
                                        isMobile ? "w-full" : "w-[150px]"
                                    )}
                                >
                                    <CalendarIcon className="mr-2 h-4 w-4" />
                                    {format(fromDate, "PP")}
                                </Button>
                            </PopoverTrigger>
                            <PopoverContent
                                className="w-auto p-0"
                                align="start"
                            >
                                <CustomDatePicker
                                    date={fromDate}
                                    setDate={(d) => {
                                        setFromDate(d);
                                        setPage(1);
                                        setIsFromOpen(false);
                                    }}
                                    restriction="none"
                                    allowPast
                                />
                            </PopoverContent>
                        </Popover>
                    </div>
                    <div
                        className={cn(
                            "flex gap-1",
                            isMobile ? "flex-col items-stretch" : "items-center"
                        )}
                    >
                        <span className="text-sm text-gray-500">To</span>
                        <Popover open={isToOpen} onOpenChange={setIsToOpen}>
                            <PopoverTrigger asChild>
                                <Button
                                    variant="outline"
                                    className={cn(
                                        "justify-start text-left font-normal",
                                        isMobile ? "w-full" : "w-[150px]"
                                    )}
                                >
                                    <CalendarIcon className="mr-2 h-4 w-4" />
                                    {format(toDate, "PP")}
                                </Button>
                            </PopoverTrigger>
                            <PopoverContent
                                className="w-auto p-0"
                                align="start"
                            >
                                <CustomDatePicker
                                    date={toDate}
                                    setDate={(d) => {
                                        setToDate(d);
                                        setPage(1);
                                        setIsToOpen(false);
                                    }}
                                    restriction="none"
                                    allowPast
                                />
                            </PopoverContent>
                        </Popover>
                    </div>
                </div>

                {selectedIds.size > 0 && (
                    <div className="mt-3 rounded-md border border-gray-200 bg-gray-50 p-3">
                        <div
                            className={cn(
                                "flex gap-3",
                                isMobile
                                    ? "flex-col"
                                    : "flex-row flex-wrap items-end justify-between"
                            )}
                        >
                            <div className="flex flex-col gap-2">
                                <span className="text-sm font-medium text-primary">
                                    {selectedIds.size} selected
                                </span>
                                <div className="flex flex-wrap items-center gap-2">
                                    <Popover
                                        open={isCalendarOpen}
                                        onOpenChange={setIsCalendarOpen}
                                    >
                                        <PopoverTrigger asChild>
                                            <Button
                                                variant="outline"
                                                className={cn(
                                                    "justify-start text-left font-normal",
                                                    isMobile
                                                        ? "w-full"
                                                        : "w-[200px]"
                                                )}
                                            >
                                                <CalendarIcon className="mr-2 h-4 w-4" />
                                                {format(rescheduleDate, "PPP")}
                                            </Button>
                                        </PopoverTrigger>
                                        <PopoverContent
                                            className="w-auto p-0"
                                            align="start"
                                        >
                                            <CustomDatePicker
                                                date={rescheduleDate}
                                                setDate={handleDateChange}
                                                restriction="none"
                                            />
                                        </PopoverContent>
                                    </Popover>
                                    <Clock className="h-4 w-4 text-gray-500" />
                                    <Select
                                        value={displayHour.toString()}
                                        onValueChange={handleHourChange}
                                    >
                                        <SelectTrigger className="w-[70px]">
                                            <SelectValue placeholder="HH" />
                                        </SelectTrigger>
                                        <SelectContent>
                                            {HOUR_OPTIONS.map((hour) => (
                                                <SelectItem
                                                    key={hour}
                                                    value={hour.toString()}
                                                >
                                                    {hour
                                                        .toString()
                                                        .padStart(2, "0")}
                                                </SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                    <Select
                                        value={minute.toString()}
                                        onValueChange={handleMinuteChange}
                                    >
                                        <SelectTrigger className="w-[70px]">
                                            <SelectValue placeholder="MM" />
                                        </SelectTrigger>
                                        <SelectContent>
                                            {MINUTE_OPTIONS.map((min) => (
                                                <SelectItem
                                                    key={min}
                                                    value={min.toString()}
                                                >
                                                    {min
                                                        .toString()
                                                        .padStart(2, "0")}
                                                </SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                    <Select
                                        value={period}
                                        onValueChange={handlePeriodChange}
                                    >
                                        <SelectTrigger className="w-[70px]">
                                            <SelectValue placeholder="AM/PM" />
                                        </SelectTrigger>
                                        <SelectContent>
                                            <SelectItem value="AM">AM</SelectItem>
                                            <SelectItem value="PM">PM</SelectItem>
                                        </SelectContent>
                                    </Select>
                                </div>
                                {isTooSoon && (
                                    <p className="text-xs text-red-500">
                                        Pick a time at least 5 minutes from now.
                                    </p>
                                )}
                            </div>
                            <div className={cn("flex gap-2", isMobile && "w-full")}>
                                <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={clearSelection}
                                    disabled={submitting}
                                    className={cn(isMobile && "flex-1")}
                                >
                                    Clear
                                </Button>
                                <Button
                                    size="sm"
                                    onClick={handleRescheduleClick}
                                    disabled={submitting}
                                    className={cn(isMobile && "flex-1")}
                                >
                                    <CalendarClock className="h-4 w-4 mr-1" />
                                    Reschedule selected
                                </Button>
                            </div>
                        </div>
                    </div>
                )}
            </CardHeader>

            <CardContent>
                <Table>
                    <TableHeader>
                        <TableRow>
                            <TableHead className="w-10">
                                <Checkbox
                                    variant="radix"
                                    checked={allVisibleSelected}
                                    onChange={toggleSelectAll}
                                />
                            </TableHead>
                            <TableHead>Owner</TableHead>
                            <TableHead>Listing / Campaign</TableHead>
                            {sortableHead("Platform", "platform")}
                            {sortableHead("Status", "status")}
                            {sortableHead(
                                "Retries",
                                "retryCount",
                                "text-center"
                            )}
                            <TableHead>Last Failure</TableHead>
                            {sortableHead("Last Failed", "lastFailedAt")}
                            {sortableHead("Next Attempt", "scheduledFor")}
                        </TableRow>
                    </TableHeader>
                    <TableBody>
                        {loading ? (
                            <TableRow>
                                <TableCell
                                    colSpan={COLUMN_COUNT}
                                    className="py-12"
                                >
                                    <Spinner loadingMessage="Loading failed posts..." />
                                </TableCell>
                            </TableRow>
                        ) : posts.length > 0 ? (
                            posts.map((post) => {
                                const isSelected = selectedIds.has(post.postId);
                                return (
                                    <TableRow
                                        key={post.postId}
                                        data-state={
                                            isSelected ? "selected" : undefined
                                        }
                                    >
                                        <TableCell>
                                            <Checkbox
                                                variant="radix"
                                                checked={isSelected}
                                                onChange={(checked) =>
                                                    toggleOne(
                                                        post.postId,
                                                        checked
                                                    )
                                                }
                                            />
                                        </TableCell>
                                        <TableCell>
                                            <div className="font-medium text-primary">
                                                {post.owner.name}
                                            </div>
                                            <div className="text-xs text-gray-500">
                                                {post.owner.email}
                                            </div>
                                        </TableCell>
                                        <TableCell>
                                            <div className="font-medium">
                                                {post.listingLabel}
                                            </div>
                                            <div className="text-xs text-gray-500">
                                                {post.campaignId ?? ""}
                                            </div>
                                        </TableCell>
                                        <TableCell>
                                            {formatPlatform(post.platform)}
                                        </TableCell>
                                        <TableCell>
                                            <Badge
                                                variant={
                                                    post.status ===
                                                    FailedPostStatus.Error
                                                        ? "destructive"
                                                        : "secondary"
                                                }
                                            >
                                                {STATUS_LABEL[post.status]}
                                            </Badge>
                                        </TableCell>
                                        <TableCell className="text-center">
                                            {post.retryCount}
                                        </TableCell>
                                        <TableCell className="max-w-[320px] min-w-[220px]">
                                            <span
                                                title={post.lastFailureReason}
                                                className="block whitespace-normal break-words text-gray-600"
                                            >
                                                {post.lastFailureReason || "—"}
                                            </span>
                                        </TableCell>
                                        <TableCell className="whitespace-nowrap">
                                            {post.lastFailedAt
                                                ? formatDateTime(
                                                      post.lastFailedAt
                                                  )
                                                : "—"}
                                        </TableCell>
                                        <TableCell className="whitespace-nowrap">
                                            {post.status ===
                                                FailedPostStatus.Retrying &&
                                            post.scheduledFor
                                                ? formatDateTime(
                                                      post.scheduledFor
                                                  )
                                                : "—"}
                                        </TableCell>
                                    </TableRow>
                                );
                            })
                        ) : (
                            <TableRow>
                                <TableCell
                                    colSpan={COLUMN_COUNT}
                                    className="py-6 text-center text-gray-500"
                                >
                                    No failed posts found
                                </TableCell>
                            </TableRow>
                        )}
                    </TableBody>
                </Table>
            </CardContent>

            {totalPages > 1 && (
                <CardFooter>
                    <Pagination className="w-full">
                        <PaginationContent>
                            <PaginationItem>
                                <PaginationPrevious
                                    onClick={() => setPage(Math.max(page - 1, 1))}
                                    className={cn(
                                        page === 1 &&
                                            "pointer-events-none opacity-50"
                                    )}
                                />
                            </PaginationItem>
                            {Array.from(
                                { length: Math.min(5, totalPages) },
                                (_, i) => {
                                    let pageNum: number | null = null;
                                    if (i === 0) pageNum = 1;
                                    else if (i === 4) pageNum = totalPages;
                                    else if (totalPages <= 5) pageNum = i + 1;
                                    else if (page <= 3) pageNum = i + 1;
                                    else if (page >= totalPages - 2)
                                        pageNum = totalPages - 4 + i;
                                    else pageNum = page - 1 + i;
                                    if (!pageNum) {
                                        return (
                                            <PaginationItem key={`e-${i}`}>
                                                <PaginationEllipsis />
                                            </PaginationItem>
                                        );
                                    }
                                    return (
                                        <PaginationItem key={pageNum}>
                                            <PaginationLink
                                                isActive={page === pageNum}
                                                onClick={() => setPage(pageNum!)}
                                            >
                                                {pageNum}
                                            </PaginationLink>
                                        </PaginationItem>
                                    );
                                }
                            )}
                            <PaginationItem>
                                <PaginationNext
                                    onClick={() =>
                                        setPage(Math.min(page + 1, totalPages))
                                    }
                                    className={cn(
                                        page === totalPages &&
                                            "pointer-events-none opacity-50"
                                    )}
                                />
                            </PaginationItem>
                        </PaginationContent>
                    </Pagination>
                </CardFooter>
            )}

            <ReconfirmOverlay
                isOpen={confirmOpen}
                title="Reschedule selected posts?"
                message={`This will reschedule ${selectedIds.size} post${
                    selectedIds.size > 1 ? "s" : ""
                } to publish at ${format(rescheduleDate, "PPp")}.`}
                messageTwo="Each post's retry count resets to 0."
                confirmText="Reschedule"
                onClose={() => setConfirmOpen(false)}
                onConfirm={handleConfirm}
            />
        </Card>
    );
};

export default FailedPostsList;
